import axios from 'axios';

/**
 * Interview Invitations — typed API client ("Pakviesti pokalbiui").
 *
 * Mirrors the freelance-gigs API client shape so the messaging UI can be
 * built on the same patterns. All methods unwrap the `{ success, data }`
 * envelope and throw on `success === false`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type InterviewInvitationStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'withdrawn';

export interface InterviewInvitation {
  id: number;
  employer_id: number;
  candidate_id: number;
  intro_message: string | null;
  status: InterviewInvitationStatus;
  accepted_at: string | null;
  declined_at: string | null;
  withdrawn_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InterviewInvitationWithCandidate extends InterviewInvitation {
  candidate_email: string | null;
  candidate_first_name: string | null;
  candidate_last_name: string | null;
  unread_count: number;
}

export interface InterviewInvitationWithEmployer extends InterviewInvitation {
  employer_email: string | null;
  employer_first_name: string | null;
  employer_last_name: string | null;
  employer_company_name: string | null;
  unread_count: number;
}

export interface InterviewInvitationMessage {
  id: number;
  invitation_id: number;
  from_user_id: number;
  body: string;
  read_at: string | null;
  created_at: string;
}

export interface OtherPartyIdentity {
  id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string>;
  message?: string;
}

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await promise;
  if (!res.data?.success) {
    const err: any = new Error(res.data?.error || 'Request failed');
    err.fieldErrors = res.data?.errors;
    throw err;
  }
  return res.data.data as T;
}

export const interviewInvitationsApi = {
  // ─── Employer ─────────────────────────────────────────────────────────────
  create: (candidateId: number, introMessage?: string) =>
    unwrap<{ invitation: InterviewInvitation; reused: boolean }>(
      axios.post(
        `${API_URL}/interview-invitations`,
        {
          candidate_id: candidateId,
          intro_message: introMessage ?? null,
        },
        { headers: authHeaders() },
      ),
    ),

  myOutgoing: () =>
    unwrap<{ invitations: InterviewInvitationWithCandidate[] }>(
      axios.get(`${API_URL}/interview-invitations/employer`, {
        headers: authHeaders(),
      }),
    ),

  /**
   * Cheap probe used to render the right button state on the candidate-detail
   * page. Returns `{ invitation: null }` when the employer hasn't reached out
   * to this candidate yet.
   */
  check: (candidateId: number) =>
    unwrap<{ invitation: InterviewInvitation | null }>(
      axios.get(`${API_URL}/interview-invitations/check`, {
        params: { candidate_id: candidateId },
        headers: authHeaders(),
      }),
    ),

  withdraw: (id: number) =>
    unwrap<{ invitation: InterviewInvitation }>(
      axios.post(
        `${API_URL}/interview-invitations/${id}/withdraw`,
        {},
        { headers: authHeaders() },
      ),
    ),

  // ─── Candidate ────────────────────────────────────────────────────────────
  myIncoming: () =>
    unwrap<{ invitations: InterviewInvitationWithEmployer[] }>(
      axios.get(`${API_URL}/interview-invitations/employee`, {
        headers: authHeaders(),
      }),
    ),

  accept: (id: number) =>
    unwrap<{ invitation: InterviewInvitation }>(
      axios.post(
        `${API_URL}/interview-invitations/${id}/accept`,
        {},
        { headers: authHeaders() },
      ),
    ),

  decline: (id: number) =>
    unwrap<{ invitation: InterviewInvitation }>(
      axios.post(
        `${API_URL}/interview-invitations/${id}/decline`,
        {},
        { headers: authHeaders() },
      ),
    ),

  // ─── Both parties ─────────────────────────────────────────────────────────
  getById: (id: number) =>
    unwrap<{
      invitation: InterviewInvitation;
      other_party: OtherPartyIdentity | null;
      role: 'employer' | 'candidate';
    }>(
      axios.get(`${API_URL}/interview-invitations/${id}`, {
        headers: authHeaders(),
      }),
    ),

  messages: (id: number) =>
    unwrap<{ messages: InterviewInvitationMessage[] }>(
      axios.get(`${API_URL}/interview-invitations/${id}/messages`, {
        headers: authHeaders(),
      }),
    ),

  postMessage: (id: number, body: string) =>
    unwrap<{ message: InterviewInvitationMessage }>(
      axios.post(
        `${API_URL}/interview-invitations/${id}/messages`,
        { body },
        { headers: authHeaders() },
      ),
    ),

  /**
   * Single COUNT(*) probe used by the global nav to decorate the
   * "Pasiūlymai" entry with a badge.
   */
  unreadSummary: () =>
    unwrap<{
      unread_messages: number;
      pending_invitations: number;
      total: number;
    }>(
      axios.get(`${API_URL}/interview-invitations/unread-summary`, {
        headers: authHeaders(),
      }),
    ),
};
